import cn from "classnames";
import { useState, useEffect } from "react";
import { Button } from "@rshbintech.dboul/core-ui";
import { confirmationApi, ConfirmationApiTypes } from "@rshbintech.dboul/http";
import { useAppTranslation } from "../../hooks";
import { usePosition } from "../position-context";
import { Loader } from "../loader";
import {
  MS_SETTINGS_PERMISSIONS,
  BUTTON_TITLES,
  REVOKING_TYPES,
  TRUE,
} from "./constants";
import { RevokingSignatureProps } from "./types";
import styles from "./styles.scss";

export const RevokingSignature = ({
  className,
  buttonText,
  documentId,
  documentType,
  revokeType,
  onErrorRevoking,
  onSuccessRevoking,
  onClickRevokingButton,
  dataAttributesRevokingSignatureButton,
}: RevokingSignatureProps) => {
  const [isRevoking, setIsRevoking] = useState(false);
  const [isRevokingPermitted, setIsRevokingPermitted] = useState(false);
  const { translate } = useAppTranslation();

  const position = usePosition();

  const getPermissions = async () => {
    try {
      const userPermissionsResponse = await confirmationApi.api.getSettings([
        MS_SETTINGS_PERMISSIONS[revokeType],
      ]);

      const permissions = userPermissionsResponse.data;
      const hasRevokePermission = permissions.some(
        (permission) =>
          permission.name === MS_SETTINGS_PERMISSIONS[revokeType] &&
          permission.value === TRUE
      );

      setIsRevokingPermitted(hasRevokePermission);
    } catch (error) {
      onErrorRevoking?.(
        (error as Record<string, ConfirmationApiTypes.ErrorMessage>)?.error
          ?.description
      );
    }
  };

  const revokeSignature = async () => {
    setIsRevoking(true);

    try {
      if (revokeType === REVOKING_TYPES.ALL_SIGNATURES) {
        await confirmationApi.api.revokeAll(documentType, String(documentId));
      } else if (revokeType === REVOKING_TYPES.OWN_SIGNATURE) {
        await confirmationApi.api.revoke(documentType, String(documentId));
      }

      onSuccessRevoking?.();
    } catch (error) {
      onErrorRevoking?.(
        (error as Record<string, ConfirmationApiTypes.ErrorMessage>)?.error
          ?.description
      );
    }

    setIsRevoking(false);
  };

  const handlerRevokeSignature = async () => {
    if (isRevokingPermitted) {
      onClickRevokingButton?.();
      await revokeSignature();
    }
  };

  useEffect(() => {
    getPermissions();
  }, []);

  return (
    <div
      data-test-id="revoking-signature"
      className={cn(styles.container, className)}
    >
      {isRevoking ? (
        <Loader dataTestId="revoking-signature-loader" />
      ) : (
        <Button
          className={cn(styles["revoking-signature-button"], styles[position])}
          dataTestId="revoking-button"
          type="outline-danger"
          htmlType="button"
          onClick={handlerRevokeSignature}
          disabled={!isRevokingPermitted}
          data-rptracker-event={
            dataAttributesRevokingSignatureButton?.dataRptrackerEvent
          }
          data-rptracker-category={
            dataAttributesRevokingSignatureButton?.dataRptrackerCategory
          }
          data-rptracker-action={
            dataAttributesRevokingSignatureButton?.dataRptrackerAction
          }
          data-rptracker-value={
            dataAttributesRevokingSignatureButton?.dataRptrackerValue
          }
        >
          {buttonText ?? translate(BUTTON_TITLES[revokeType])}
        </Button>
      )}
    </div>
  );
};
